import { BrowserSupport } from '@tableau/browser-support';
import { createReportEventUnauthenticatedParams, ReportEventUnauthenticatedParams ,TelemetryForwarder, ValidPropertyValueType } from '@tableau/typescript-telemetry-forwarder';
import { defaultMemoize } from 'reselect';
import { v4 as uuidv4 } from 'uuid';

import { SessionService } from '../../../src/commonInfra/services/SessionService';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';

export type BaseTelemetryEvent = { type: string };
export type AnalyticsEventProperties = { [K: string]: ValidPropertyValueType };

/**
 * Only specific types (validPropertyValueType) are allowed as values for events.
 * Custom telemetry event interfaces can extend this type to ensure that type-safety.
 */
export type ValidTelemetryEvent = BaseTelemetryEvent & AnalyticsEventProperties;

/**
 * TelemetryService handles sending telemetry events to the Google Big Query database.
 *
 * App-wide event properties get set through the reporter and eventFactory
 * Usage example: SiteSpace.tsx for React
 *
 * For setting up events in Google BigQuery, see https://mytableau.tableaucorp.com/x/0hOHBw
 */

function sendEvent<T extends ValidTelemetryEvent>(event: T): void {
  // Telemetry is fire-and-forget: a failure inside getCommonProperties (e.g. session lookups
  // throwing because the session got torn down between the user click and the emit) or inside
  // the forwarder must never propagate to the caller. Many sendEvent callers fire right before
  // the primary user action (a save, a publish, a tag change) and downstream error handling --
  // notably ReactActionDialog.doAction -- can silently swallow exceptions, which would surface
  // to the user as "nothing happened" instead of the action they actually requested. So we wrap
  // the whole emit path in try/catch and log instead of throwing.
  try {
    const { type, ...customProps } = event;

    const properties: AnalyticsEventProperties = {
      ...customProps,
      ...getCommonProperties()
    };

    Object.keys(properties).forEach(key => {
      // since remove waitingForSession or getSessionInfo before we send telemetry, values of sessionInfo could be null
      // vizportal java code does not allow null value, so need convert to empty string
      if (properties[key] === undefined || properties[key] === null) {
        properties[key] = '';
      }
    });

    // java-event-client use NetworkUtilities.getHostName() to get hostName, so we do not need to add hostName
    // https://gitlab.tableausoftware.com/Product-Analytics/java-event-client/blob/master/src/main/java/com/tableau/telemetry/event/AEvent.java#L27
    const params: ReportEventUnauthenticatedParams = createReportEventUnauthenticatedParams(type, 'vizportalclient', properties);
    getTelemetryForwarder().send(params);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[Telemetry.sendEvent] swallowed exception; user action will continue.', { eventType: event?.type, err });
  }
}

function getCommonProperties(): AnalyticsEventProperties {
  return {
    uuid: uuidv4(),
    user_id: getSessionManagementService().sessionUserId(),
    site_id: getSessionManagementService().sessionSiteLuid(),
    site_role_id: getSessionManagementService().sessionUserInfo()?.siteRoleId,
    site_role: getSessionManagementService().sessionSiteRole(),
    session_id: SessionService.getSessionId(),
    is_mobile: BrowserSupport.IsMobileBrowser()
  };
}

const getTelemetryForwarder = defaultMemoize(() => new TelemetryForwarder());

function getSessionManagementService(): SessionManagementService {
  return getSessionManagementServiceInstance();
}

export const Telemetry = {
  sendEvent,
  getCommonProperties,
  getTelemetryForwarder
};

export type Telemetry = typeof Telemetry;
